import { Fragment, useEffect, useRef, useState } from 'react'
import { Box, Typography, Chip, Button, Stack } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Feature, Plan, PLAN_LABEL } from '../core/features'
import { getPlanAccent } from './planDrawerColors'
import { buildBuyUrl } from '../utils/externalLinks'
import PlanInfoDrawer from './PlanInfoDrawer'
import { usePlan } from '../hooks/usePlan'
import { useAnalytics } from '../hooks/useAnalytics'
import { useFeatureAnalytics } from '../hooks/useFeatureAnalytics'
import { errorClass } from '../utils/analytics'
import { licensingAPI } from '../services/api'

interface UpgradePromptProps {
  requiredPlan: Plan
  message?: string
  /** One line instead of a card, for a teaser that sits under real data:
   *  the numbers above it are the argument, so the prompt stays out of
   *  the way (spec 2026-09-21, section 1). */
  compact?: boolean
  /** The feature behind the lock: its key goes on the buy link and every
   *  analytics event. With one, an admin who has not used its trial is
   *  offered it here (spec 2026-09-21, section 3), unless offerTrial is off. */
  feature?: Feature
  offerTrial?: boolean
  /** Analytics surface for the buy click and trial. When the prompt is the gate
   *  itself (no PlanGate around it), it also reports Plan - FeatureBlocked once
   *  under this surface, unless reportShown is off. */
  surface?: string
  /** Off when a PlanGate around the prompt already reports the lock. */
  reportShown?: boolean
}

export default function UpgradePrompt({
  requiredPlan,
  message,
  compact,
  feature,
  offerTrial: trialAllowed = true,
  surface,
  reportShown = true,
}: UpgradePromptProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const color = getPlanAccent(requiredPlan, theme)
  const planLabel = PLAN_LABEL[requiredPlan]
  const [drawerOpen, setDrawerOpen] = useState(false)
  const buyPlan = requiredPlan === 'enterprise' ? 'enterprise' : 'pro'
  const buySource = feature ? `app-gate-${feature}` : 'app-upgrade-prompt'
  const buyUrl = buildBuyUrl({ plan: buyPlan, src: buySource })
  const { trackPlan, EventAction } = useAnalytics()
  const { trackFeatureBlocked } = useFeatureAnalytics()
  const analyticsSurface = surface ?? 'upgrade_prompt'
  const handleBuyClick = () =>
    trackPlan(EventAction.VIEW, {
      surface: analyticsSurface,
      operation: 'open_buy_link',
      selected_plan: buyPlan,
      ...(feature ? { feature } : {}),
      src: buySource,
    })
  // Once per mount: the prompt re-renders as the panel around it loads.
  const shownTracked = useRef(false)
  useEffect(() => {
    if (!feature || !surface || !reportShown || shownTracked.current) return
    shownTracked.current = true
    trackFeatureBlocked(feature, { surface, operation: 'teaser', gate_mode: 'teaser' })
  }, [feature, surface, reportShown, trackFeatureBlocked])
  const trialContext = { operation: 'start_feature_trial', feature, surface: analyticsSurface }
  const { plan, features, entitlement } = usePlan()
  const queryClient = useQueryClient()
  const [trialRefusal, setTrialRefusal] = useState<string | null>(null)
  // The activation service decides whether a trial is granted, so the offer
  // stands until it has answered for this feature. The route is admin only,
  // and a reader without the rights is told so by the answer rather than by
  // a permission lookup here: this prompt renders in a dozen places, and a
  // second context to mount is a cost every one of them would pay.
  const trialExpired = (entitlement?.expired_trial_features ?? []).includes(feature ?? '')
  const trialRunning = (entitlement?.trial_features ?? []).some((f) => f.feature === feature)
  const trial = useMutation({
    mutationFn: () => {
      trackPlan(EventAction.START, trialContext)
      return licensingAPI.featureTrial(feature as string)
    },
    onSuccess: (res) => {
      if (res.data.result === 'denied') {
        trackPlan(EventAction.FAIL, { ...trialContext, reason: res.data.reason ?? 'denied' })
        setTrialRefusal(res.data.reason ?? 'denied')
        return
      }
      trackPlan(EventAction.COMPLETE, { ...trialContext, result: res.data.result })
      setTrialRefusal(null)
      // Everything the plan touches, not just the plan itself: the panels
      // around this prompt hold answers the server shaped for a reader
      // without the feature, and they would keep showing the lock until
      // something else happened to refetch them.
      queryClient.invalidateQueries()
    },
    onError: (error) => {
      trackPlan(EventAction.FAIL, { ...trialContext, error_class: errorClass(error) })
      setTrialRefusal(
        (error as { response?: { status?: number } })?.response?.status === 403
          ? 'needsAdmin'
          : 'failed'
      )
    },
  })
  const offerTrial =
    trialAllowed && feature != null && !trialRunning && !trialExpired && trialRefusal === null
  const trialNote = trialRefusal
    ? t(
        trialRefusal === 'failed'
          ? 'upgradePrompt.trialFailed'
          : trialRefusal === 'needsAdmin'
            ? 'upgradePrompt.trialNeedsAdmin'
            : // The service says this install has already had it: the reader
              // gets the ended wording, not "no trial exists for this".
              trialRefusal === 'trial_already_used'
              ? 'upgradePrompt.trialEnded'
              : 'upgradePrompt.trialUnavailable'
      )
    : trialExpired
      ? t('upgradePrompt.trialEnded')
      : null
  // One lead action, the rest subordinate: the free trial while it is on
  // offer, the purchase once it is not. Three buttons of equal weight read
  // as a disabled row and get skipped.
  const trialButton = offerTrial ? (
    <Button
      variant="contained"
      size="small"
      disableElevation
      disabled={trial.isPending}
      onClick={() => trial.mutate()}
      sx={{ textTransform: 'none' }}
    >
      {t('upgradePrompt.tryFree')}
    </Button>
  ) : null
  const drawer = (
    <PlanInfoDrawer
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      plan={plan}
      initialSelectedPlan={requiredPlan}
      features={features}
      entitlement={entitlement}
    />
  )
  if (compact) {
    return (
      <Fragment>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            px: 1.5,
            py: 1.25,
            // Tinted panel rather than a dashed outline: dashed reads as an
            // empty slot waiting for content, which is how this strip came
            // to be missed under the counts. No shadow, so it stays quieter
            // than the real data above it.
            border: '1px solid',
            borderColor: alpha(color, 0.28),
            bgcolor: alpha(color, 0.05),
            borderRadius: 1.5,
          }}
        >
          <Lock size={16} color={color} style={{ flexShrink: 0 }} />
          {/* A basis wide enough that the text claims a row of its own in a
              narrow container (the file details pane), instead of wrapping to
              four lines beside the buttons. */}
          <Typography variant="body2" sx={{ color: 'text.primary', flex: '1 1 260px' }}>
            {message ?? t('upgradePrompt.defaultMessage', { plan: planLabel })}
            {trialNote != null && ` ${trialNote}`}
          </Typography>
          {/* Right of the text when both fit on one row; at phone width the
              text claims the row and the buttons wrap under it, where they
              line up with its left edge rather than hanging off the right. */}
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ alignItems: 'center', flexWrap: 'wrap', ml: { xs: 0, sm: 'auto' } }}
          >
            {trialButton}
            {/* The purchase is offered at every lock, not only in the drawer:
                a reader whose trial has ended has no other way out of this
                panel. It leads once the trial is no longer on offer. */}
            <Button
              component="a"
              href={buyUrl}
              onClick={handleBuyClick}
              target="_blank"
              rel="noreferrer"
              variant={trialButton ? 'outlined' : 'contained'}
              disableElevation
              size="small"
              sx={{ textTransform: 'none' }}
            >
              {t('plan.buyLink', { plan: planLabel })}
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => setDrawerOpen(true)}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              {t('upgradePrompt.learnMore')}
            </Button>
          </Stack>
        </Box>
        {drawer}
      </Fragment>
    )
  }
  return (
    <Fragment>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          py: 3,
          px: 2,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
          boxShadow: (theme) => `0 10px 30px ${alpha(theme.palette.common.black, 0.12)}`,
          textAlign: 'center',
        }}
      >
        <Lock size={24} color={color} />
        <Chip
          label={t('upgradePrompt.featureLabel', { plan: planLabel })}
          size="small"
          sx={{
            bgcolor: alpha(color, 0.12),
            color,
            border: `1px solid ${alpha(color, 0.25)}`,
            fontWeight: 700,
            fontSize: '0.7rem',
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            maxWidth: 280,
          }}
        >
          {message ?? t('upgradePrompt.defaultMessage', { plan: planLabel })}
        </Typography>
        {trialNote != null && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {trialNote}
          </Typography>
        )}
        {trialButton}
        <Button
          component="a"
          href={buyUrl}
          onClick={handleBuyClick}
          target="_blank"
          rel="noreferrer"
          variant={trialButton ? 'outlined' : 'contained'}
          size="small"
          sx={{ mt: 0.5 }}
        >
          {t('upgradePrompt.buyLink')}
        </Button>
        <Button
          variant="text"
          size="small"
          onClick={() => setDrawerOpen(true)}
          sx={{
            fontSize: '0.72rem',
            color: 'text.secondary',
            textTransform: 'none',
            '&:hover': { bgcolor: 'transparent', color: 'text.primary' },
          }}
        >
          {t('upgradePrompt.learnMore')}
        </Button>
      </Box>
      {drawer}
    </Fragment>
  )
}
